import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarCheck,
  HardDrive,
  Pencil,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { usePermission } from '@/hooks/use-permission';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useContract,
  useContractProfitability,
  useRenewContract,
  useRollForwardHours,
  useSaveVisit,
  useTerminateContract,
} from './api';
import { ContractDialog } from './contract-dialog';
import {
  ASSET_TYPE_LABELS,
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUS_VARIANT,
  CONTRACT_TYPE_LABELS,
} from './labels';
import type { Contract } from './types';

export function ContractDetailPage() {
  const { contractId } = useParams<{ contractId: string }>();
  const { data: contract, isLoading } = useContract(contractId);
  const [editing, setEditing] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [terminating, setTerminating] = useState(false);

  if (isLoading) return <TableSkeleton columns={3} />;
  if (!contract) return null;

  const live = ['ACTIVE', 'EXPIRING'].includes(contract.status);

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Contracts', to: '/service/contracts' },
          { label: contract.number },
        ]}
        title={contract.name}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-xs">{contract.number}</span>
            <span>·</span>
            <span>{contract.company.name}</span>
            <span>·</span>
            <span>{CONTRACT_TYPE_LABELS[contract.contractType]}</span>
            <span>·</span>
            <span>
              {formatDate(contract.startDate)} – {formatDate(contract.endDate)}
            </span>
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={CONTRACT_STATUS_VARIANT[contract.status]}>
              {CONTRACT_STATUS_LABELS[contract.status]}
            </Badge>
            <Can permission="service.contract.approve">
              {live && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setRenewing(true)}>
                    <RefreshCw className="h-4 w-4" />
                    Renew
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setTerminating(true)}>
                    <XCircle className="h-4 w-4" />
                    Terminate
                  </Button>
                </>
              )}
            </Can>
            <Can permission="service.contract.update">
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            </Can>
          </div>
        }
      />

      {contract.status === 'TERMINATED' && (
        <div className="mb-5 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
          <span className="font-medium text-destructive">Terminated</span>
          {contract.terminationEffectiveDate &&
            ` with effect from ${formatDate(contract.terminationEffectiveDate)}`}
          {contract.terminationReason && ` — ${contract.terminationReason}`}
        </div>
      )}

      {contract.daysRemaining >= 0 && contract.daysRemaining <= 60 && live && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            This contract ends in {contract.daysRemaining}{' '}
            {contract.daysRemaining === 1 ? 'day' : 'days'}.
            {contract.renewalValue &&
              ` A renewal at the agreed uplift would be ${formatMoney(contract.renewalValue, contract.currency)}.`}
          </span>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <HourBanks contract={contract} />
          <Visits contract={contract} />
          <Profitability contract={contract} />
        </div>

        <div className="space-y-5">
          <Coverage contract={contract} />
          <Assets contract={contract} />
        </div>
      </div>

      {editing && <ContractDialog contract={contract} onClose={() => setEditing(false)} />}
      {renewing && <RenewDialog contract={contract} onClose={() => setRenewing(false)} />}
      {terminating && <TerminateDialog contract={contract} onClose={() => setTerminating(false)} />}
    </div>
  );
}

function HourBanks({ contract }: { contract: Contract }) {
  const roll = useRollForwardHours(contract.id);
  const banks = contract.hourBanks ?? [];

  if (banks.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Support hours</CardTitle>
          <CardDescription>
            No prepaid hours on this contract, so support time is billed as it is logged.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Support hours</CardTitle>
        <CardDescription>
          One bank per billing period. Time logged on this client&rsquo;s tickets draws on the
          current one and is not billed again.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {banks.map((bank) => {
          const total = (bank.allocatedHours ?? 0) + (bank.carriedForwardHours ?? 0);
          return (
            <div
              key={bank.id}
              className={cn('rounded-lg border p-3', bank.isCurrent && 'border-primary/40 bg-primary/5')}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-sm">
                  <span className="font-medium">
                    {formatDate(bank.periodStart)} – {formatDate(bank.periodEnd)}
                  </span>
                  {bank.isCurrent && (
                    <Badge variant="default" className="ml-2 text-[10px]">
                      current
                    </Badge>
                  )}
                </div>
                <div className="tabular text-sm">
                  <span className={cn('font-semibold', bank.overrun && 'text-destructive')}>
                    {bank.remainingHours}h
                  </span>
                  <span className="text-muted-foreground"> left of {total}h</span>
                </div>
              </div>

              <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    'h-full rounded-full',
                    (bank.consumedPercent ?? 0) >= 100
                      ? 'bg-destructive'
                      : (bank.consumedPercent ?? 0) >= 80
                        ? 'bg-amber-500'
                        : 'bg-primary',
                  )}
                  style={{ width: `${Math.min(100, bank.consumedPercent ?? 0)}%` }}
                />
              </div>

              <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {bank.consumedHours}h used
                  {(bank.carriedForwardHours ?? 0) > 0 &&
                    ` · ${bank.carriedForwardHours}h carried in`}
                  {bank.consumedPercent !== null && ` · ${bank.consumedPercent}%`}
                </span>
                {new Date(bank.periodEnd) < new Date() && (bank.remainingHours ?? 0) > 0 && (
                  <Can permission="service.contract.update">
                    <button
                      className="text-primary hover:underline"
                      onClick={() => roll.mutate(bank.id)}
                    >
                      Roll unused hours forward
                    </button>
                  </Can>
                )}
              </div>

              {bank.overrun && (
                <p className="mt-1.5 text-xs text-destructive">
                  This period is overrun. The extra hours were delivered but not prepaid.
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function Visits({ contract }: { contract: Contract }) {
  const save = useSaveVisit(contract.id);
  const { data: users = [] } = useUserOptions();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ scheduledDate: '', visitType: '', assigneeId: '' });

  const visits = contract.visits ?? [];
  const allowance = contract.visitAllowance;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base">Service visits</CardTitle>
          <CardDescription>
            {allowance && allowance.free > 0
              ? `${allowance.freeUsed} of ${allowance.free} included visits used${
                  allowance.chargeableUsed > 0
                    ? ` · ${allowance.chargeableUsed} charged, ${formatMoney(allowance.chargedTotal, contract.currency)}`
                    : ''
                }`
              : 'Planned health checks and preventive maintenance.'}
          </CardDescription>
          {allowance && (allowance.perVisit !== '0.00' || allowance.freeUntil) && (
            <p className="mt-1 text-xs text-muted-foreground">
              {allowance.freeUntil && `Free until ${formatDate(allowance.freeUntil)}. `}
              {allowance.perVisit !== '0.00' &&
                `Beyond the included visits, ${formatMoney(allowance.perVisit, contract.currency)} each.`}
            </p>
          )}
        </div>
        <Can permission="service.contract.update">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <CalendarCheck className="h-4 w-4" />
            Schedule
          </Button>
        </Can>
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <div className="space-y-3 rounded-lg border p-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  className="h-8"
                  value={form.scheduledDate}
                  onChange={(event) => setForm({ ...form, scheduledDate: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Input
                  className="h-8"
                  placeholder="Health check"
                  value={form.visitType}
                  onChange={(event) => setForm({ ...form, visitType: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Who is going</Label>
                <Select
                  className="h-8 text-sm"
                  value={form.assigneeId}
                  onChange={(event) => setForm({ ...form, assigneeId: event.target.value })}
                >
                  <option value="">Nobody yet</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || !form.scheduledDate}
                onClick={async () => {
                  await save.mutateAsync({
                    scheduledDate: form.scheduledDate,
                    visitType: form.visitType || undefined,
                    assigneeId: form.assigneeId || undefined,
                  });
                  setAdding(false);
                  setForm({ scheduledDate: '', visitType: '', assigneeId: '' });
                }}
              >
                Schedule visit
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {visits.length === 0 && !adding ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Nothing scheduled.</p>
        ) : (
          visits.map((visit) => (
            <div
              key={visit.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm"
            >
              <div>
                <span className="font-medium">{formatDate(visit.scheduledDate)}</span>
                {visit.visitType && (
                  <span className="ml-2 text-muted-foreground">{visit.visitType}</span>
                )}
                {visit.assignee && (
                  <span className="ml-2 text-xs text-muted-foreground">{visit.assignee.name}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {visit.chargeable ? (
                  <Badge variant="warning" className="text-[10px]">
                    Chargeable {formatMoney(visit.charge, contract.currency)}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">
                    Included
                  </Badge>
                )}
                <Badge
                  variant={
                    visit.status === 'COMPLETED'
                      ? 'success'
                      : visit.status === 'MISSED'
                        ? 'destructive'
                        : 'outline'
                  }
                >
                  {visit.status.toLowerCase()}
                </Badge>
                {visit.status === 'SCHEDULED' && (
                  <Can permission="service.contract.update">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        save.mutate({
                          id: visit.id,
                          scheduledDate: visit.scheduledDate,
                          visitType: visit.visitType ?? undefined,
                          status: 'COMPLETED',
                        })
                      }
                    >
                      Mark done
                    </Button>
                  </Can>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function Profitability({ contract }: { contract: Contract }) {
  const { can } = usePermission();
  const allowed = can('finance.cost.view');
  const { data } = useContractProfitability(contract.id, allowed);

  if (!allowed || !data) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Is this contract worth it?</CardTitle>
        <CardDescription>
          Contract value against what supporting it has cost. Every minute counts here — hours a
          client prepaid still cost an engineer&rsquo;s day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.losingMoney && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <span>
              Support has cost more than the contract is worth. This one needs repricing at
              renewal, not renewing on the same terms.
            </span>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-4">
          <Mini label="Value" value={formatMoney(data.value, data.currency)} />
          <Mini label="Support cost" value={formatMoney(data.supportCost, data.currency)} />
          <Mini
            label="Profit"
            value={formatMoney(data.profit, data.currency)}
            tone={data.losingMoney ? 'bad' : 'good'}
          />
          <Mini
            label="Margin"
            value={data.marginPercent === null ? '—' : `${data.marginPercent}%`}
            tone={data.losingMoney ? 'bad' : undefined}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3 text-sm">
          <Mini label="Hours delivered" value={`${data.hoursDelivered}h`} />
          <Mini label="From the bank" value={`${data.hoursFromBank}h`} />
          <Mini label="Outside the bank" value={`${data.hoursOutsideBank}h`} />
        </div>

        {data.byPerson.length > 0 && (
          <div className="border-t pt-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Who has been supporting them
            </div>
            <ul className="space-y-1.5">
              {data.byPerson.map((person) => (
                <li key={person.userId} className="flex items-center justify-between text-sm">
                  <span>{person.name}</span>
                  <span className="tabular text-muted-foreground">
                    {person.hours}h · {formatMoney(person.cost, data.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Coverage({ contract }: { contract: Contract }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Coverage
        </CardTitle>
        <CardDescription>This text is shown on every ticket for this client.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Included
          </div>
          <p className="mt-0.5 whitespace-pre-line">
            {contract.coverageIncluded ?? 'Not written down yet.'}
          </p>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Excluded
          </div>
          <p className="mt-0.5 whitespace-pre-line text-muted-foreground">
            {contract.coverageExcluded ?? 'Nothing listed.'}
          </p>
        </div>
        <div className="space-y-1 border-t pt-3">
          <Row label="Value" value={formatMoney(contract.value, contract.currency)} />
          <Row label="Notice period" value={`${contract.renewalNoticeDays} days`} />
          <Row
            label="Renewal uplift"
            value={contract.escalationPercent ? `${contract.escalationPercent}%` : 'None agreed'}
          />
          <Row label="Auto-renews" value={contract.autoRenew ? 'Yes' : 'No'} />
          <Row label="SLA" value={contract.slaPolicy?.name ?? 'Default rules'} />
          <Row label="Owner" value={contract.owner?.name ?? 'Unassigned'} />
        </div>
      </CardContent>
    </Card>
  );
}

function Assets({ contract }: { contract: Contract }) {
  const assets = contract.assets ?? [];
  if (assets.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <HardDrive className="h-4 w-4 text-muted-foreground" />
          Assets under this contract
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {assets.map((asset) => (
          <div key={asset.id} className="flex items-center justify-between text-sm">
            <div className="min-w-0">
              <div className="truncate font-medium">{asset.name}</div>
              <div className="text-xs text-muted-foreground">
                {ASSET_TYPE_LABELS[asset.assetType]}
                {asset.provider && ` · ${asset.provider}`}
              </div>
            </div>
            {asset.expiryDate && (
              <span className="text-xs text-muted-foreground">
                expires {formatDate(asset.expiryDate)}
              </span>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RenewDialog({ contract, onClose }: { contract: Contract; onClose: () => void }) {
  const renew = useRenewContract(contract.id);
  const [value, setValue] = useState(contract.renewalValue ?? '');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Renew {contract.number}</DialogTitle>
          <DialogDescription>
            The new term starts the day after this one ends. Prepaid hours are recreated with the
            same shape, and this contract is marked renewed rather than expired.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>New contract value</Label>
            <Input
              inputMode="decimal"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {contract.escalationPercent
                ? `Pre-filled with the agreed ${contract.escalationPercent}% uplift on ${formatMoney(contract.value, contract.currency)}.`
                : 'No uplift agreed, so this matches the current value.'}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>New end date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Left blank, the new term is the same length as this one.
            </p>
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={renew.isPending}
            onClick={async () => {
              setError(null);
              try {
                await renew.mutateAsync({
                  value: value || undefined,
                  endDate: endDate || undefined,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not renew it.',
                );
              }
            }}
          >
            Create renewal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TerminateDialog({ contract, onClose }: { contract: Contract; onClose: () => void }) {
  const terminate = useTerminateContract(contract.id);
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [settlement, setSettlement] = useState<Record<string, unknown> | null>(null);

  if (settlement) {
    const details = settlement as {
      settlement: {
        noticeRequiredDays: number;
        noticeGivenDays: number;
        noticeShortfallDays: number;
        noticeCompliant: boolean;
        remainingDays: number;
        unearnedValue: string;
      };
      outstanding: { openTickets: number; assets: number; scheduledVisits: number };
    };

    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{contract.number} terminated</DialogTitle>
            <DialogDescription>
              What is left to settle. None of this is done automatically — it is a list for a
              person to work through.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <Row
              label="Notice required"
              value={`${details.settlement.noticeRequiredDays} days`}
            />
            <Row label="Notice given" value={`${details.settlement.noticeGivenDays} days`} />
            {!details.settlement.noticeCompliant && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Short by {details.settlement.noticeShortfallDays} days. That is a commercial matter,
                not a system one — but now nobody will find out later.
              </p>
            )}
            <Row
              label="Unearned portion of the term"
              value={formatMoney(details.settlement.unearnedValue, contract.currency)}
            />
            <div className="border-t pt-3">
              <Row label="Tickets still open" value={String(details.outstanding.openTickets)} />
              <Row label="Assets to hand back" value={String(details.outstanding.assets)} />
              <Row label="Visits still booked" value={String(details.outstanding.scheduledVisits)} />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Terminate {contract.number}?</DialogTitle>
          <DialogDescription>
            {contract.company.name} — this ends the contract early and works out what is left to
            settle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Effective from</Label>
            <Input
              type="date"
              value={effectiveDate}
              onChange={(event) => setEffectiveDate(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="destructive"
            disabled={terminate.isPending || reason.trim().length < 3 || !effectiveDate}
            onClick={async () => {
              setError(null);
              try {
                const result = await terminate.mutateAsync({ reason, effectiveDate });
                setSettlement(result as never);
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not terminate it.',
                );
              }
            }}
          >
            Terminate contract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-0.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function Mini({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <div className="rounded-lg border p-2.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          'tabular mt-0.5 font-semibold',
          tone === 'good' && 'text-emerald-600',
          tone === 'bad' && 'text-destructive',
        )}
      >
        {value}
      </div>
    </div>
  );
}
